/**
 * BESTIX v5.0 — Footer
 * Full BESPOKIAN-style footer with links and social
 */

import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter } from 'lucide-react';
import { cn } from '@/lib/utils';

const footerLinks = {
  events: {
    title: 'Events',
    links: [
      { label: 'Concerts', href: '/events?category=concerts' },
      { label: 'Sports', href: '/events?category=sports' },
      { label: 'Theater', href: '/events?category=theater' },
      { label: 'Festivals', href: '/events?category=festivals' },
    ],
  },
  sports: {
    title: 'Sports',
    links: [
      { label: 'NFL', href: '/events?category=sports&league=nfl' },
      { label: 'NBA', href: '/events?category=sports&league=nba' },
      { label: 'MLB', href: '/events?category=sports&league=mlb' },
      { label: 'Soccer', href: '/events?category=sports&league=soccer' },
    ],
  },
  services: {
    title: 'Services',
    links: [
      { label: 'Concierge', href: '/help' },
      { label: 'Sell Tickets', href: '/sell' },
      { label: 'Corporate Events', href: '/contact' },
      { label: 'Gift Cards', href: '/events' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About BESTIX', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  },
};

const socialLinks = [
  { icon: Instagram, href: 'https://instagram.com/bestixai', label: 'Instagram' },
  { icon: Linkedin, href: 'https://linkedin.com/company/bespokian', label: 'LinkedIn' },
  { icon: Twitter, href: 'https://twitter.com/bestixai', label: 'Twitter' },
];

interface FooterProps {
  className?: string;
}

export function Footer({ className }: FooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={cn('bg-[var(--color-navy)] text-white', className)}>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 text-white">
              <img
                src="/favicon.svg"
                alt=""
                className="h-12 w-auto shrink-0"
              />
              <div className="flex flex-col">
                <span className="font-playfair text-xl font-semibold tracking-wide">BESPOKIAN</span>
                <span className="text-[10px] tracking-[0.15em] uppercase text-white/60">TICKETS</span>
              </div>
            </Link>
            <p className="text-white/70 leading-relaxed mb-6 max-w-xs">
              Extraordinary experiences for discerning fans. Premium tickets to the world's most exclusive events.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[var(--color-gold)] hover:text-[var(--color-navy)] transition-colors"
                    aria-label={social.label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link Columns */}
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h3 className="font-medium text-white mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link 
                      to={link.href} 
                      className="text-white/60 hover:text-[var(--color-gold)] text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-white/50 text-sm">© {currentYear} BESPOKIAN Inc. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <span className="text-white/50 text-xs uppercase tracking-wide">Licensed Ticket Reseller</span>
              <span className="w-px h-4 bg-white/20" />
              <span className="text-white/50 text-xs uppercase tracking-wide">SSL Secured</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
